import { MaterialCommunityIcons } from '@expo/vector-icons';
import { DateTypeFilter } from '@gripp/shared-logic';
import { Colors, DateRangeSelector, FormContext } from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Modal, Portal, RadioButton } from 'react-native-paper';
import { mixed, string, object as yupObject } from 'yup';
import { DownloadRoutine } from './downloadRoutine';

type Props = {
  label: string;
  routineIds: string[];
  workspaceId: string;
};

export const DownloadRoutineWithDateFilter = (props: Props) => {
  const { label, routineIds, workspaceId } = props;
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });
  const [dateFilter, setDateFilter] = useState<
    | {
        dateType: DateTypeFilter;
        startDate?: string;
        endDate?: string;
      }
    | undefined
  >();
  const downloadRef = useRef<{ triggerDownload: () => void }>(null);

  const formSchema = yupObject({
    dateType: mixed<DateTypeFilter>()
      .oneOf(Object.values(DateTypeFilter))
      .required(),
    startDate: string().when('dateType', (dateType, schema) => {
      const value = Array.isArray(dateType) ? dateType[0] : dateType;
      return value === DateTypeFilter.CUSTOMDATE ? schema.required() : schema;
    }),
    endDate: string().when('dateType', (dateType, schema) => {
      const value = Array.isArray(dateType) ? dateType[0] : dateType;
      return value === DateTypeFilter.CUSTOMDATE ? schema.required() : schema;
    }),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      dateType: DateTypeFilter.ALL,
      startDate: '',
      endDate: '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    setValue,
    watch,
  } = formMethods;

  const selectedDate = watch('dateType');

  const handleDownload = (filter: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  }) => {
    setDateFilter(filter);
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleButtonPress = (event: any) => {
    // Get button position for modal positioning
    event.target.measure(
      (
        x: number,
        y: number,
        width: number,
        height: number,
        pageX: number,
        pageY: number
      ) => {
        setButtonPosition({ x: pageX, y: pageY + height });
      }
    );
    setShowModal(true);
  };

  const onDateChange = (date: DateTypeFilter) => {
    if (
      date !== DateTypeFilter.CUSTOMDATE &&
      (control._formValues.endDate || control._formValues.startDate)
    ) {
      setValue('startDate', '');
      setValue('endDate', '');
    }
    setValue('dateType', date, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const renderCustomDate = (value: string) => {
    if (value !== DateTypeFilter.CUSTOMDATE) return null;

    return (
      <View style={styles.renderCustomDate}>
        <DateRangeSelector
          control={control}
          setValue={setValue}
          styleContainer={styles.dateRangeContainer}
        />
      </View>
    );
  };

  const onSubmit = async (data: any) => {
    const filter = {
      dateType: data.dateType,
      startDate: data.startDate,
      endDate: data.endDate,
    };
    setDateFilter(filter);
    setShowModal(false);
    // Trigger download after a short delay to ensure state is updated
    setTimeout(() => {
      downloadRef.current?.triggerDownload();
    }, 100);
  };

  return (
    <View style={styles.buttonContainer}>
      <Pressable
        style={({ pressed }) => [
          styles.customButton,
          styles.buttonStyle,
          pressed && styles.pressed,
        ]}
        onPress={handleButtonPress}
      >
        <View style={{ flexDirection: 'row' }}>
          <MaterialCommunityIcons
            name="download"
            color={Colors.blackText}
            size={16}
            style={styles.icon}
          />
          <Text style={styles.label}>{label}</Text>
        </View>
      </Pressable>

      {/* Date Selection Modal */}
      <Portal>
        <Modal
          visible={showModal}
          onDismiss={handleCancel}
          contentContainerStyle={[
            styles.modal,
            {
              position: 'absolute',
              top: buttonPosition.y,
              left: buttonPosition.x,
              transform: [{ translateX: -150 }], // Center horizontally relative to button
            },
          ]}
        >
          <FormContext.Provider value={formMethods as any}>
            <View style={styles.container}>
              <View style={styles.scrollViewContainer}>
                <ScrollView
                  contentContainerStyle={styles.scrollViewContent}
                  scrollEnabled={true}
                  showsVerticalScrollIndicator={true}
                >
                  <View>
                    {Object.entries(DateTypeFilter).map(([key, value]) => {
                      const enumKey = key as keyof typeof DateTypeFilter;
                      return (
                        <Pressable
                          key={enumKey}
                          onPress={() => onDateChange(DateTypeFilter[enumKey])}
                        >
                          <View style={styles.dateFilterRowRadioButton}>
                            <View
                              style={[
                                styles.rowDateFilterContainer,
                                styles.rowRadioButton,
                              ]}
                            >
                              <Text
                                numberOfLines={1}
                                style={styles.checkboxLabel}
                              >
                                {t(`activity.filter.${value}`)}
                              </Text>
                              <RadioButton.Android
                                value={DateTypeFilter[enumKey]}
                                status={
                                  DateTypeFilter[enumKey] === selectedDate
                                    ? 'checked'
                                    : 'unchecked'
                                }
                                onPress={() =>
                                  onDateChange(DateTypeFilter[enumKey])
                                }
                              />
                            </View>
                            {renderCustomDate(value)}
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                </ScrollView>
              </View>

              <View style={styles.footer}>
                <Pressable style={styles.cancelButton} onPress={handleCancel}>
                  <Text style={styles.cancelButtonText}>
                    {t('common.cancel')}
                  </Text>
                </Pressable>
                <Pressable
                  style={[
                    styles.downloadButton,
                    {
                      backgroundColor: Colors.primary,
                    },
                  ]}
                  onPress={handleSubmit(onSubmit)}
                >
                  <Text style={styles.downloadButtonText}>
                    {t('common.download')}
                  </Text>
                </Pressable>
              </View>
            </View>
          </FormContext.Provider>
        </Modal>
      </Portal>

      {/* Hidden Download Component */}
      {dateFilter && (
        <View style={{ display: 'none' }}>
          <DownloadRoutine
            key={`${dateFilter.dateType}-${dateFilter.startDate}-${dateFilter.endDate}`}
            ref={downloadRef}
            label={label}
            routineIds={routineIds}
            workspaceId={workspaceId}
            dateFilter={dateFilter}
            hideButton={true}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    position: 'relative',
    zIndex: 9999,
  },

  icon: {
    marginRight: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: -0.25,
  },
  customButton: {
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  pressed: {
    backgroundColor: Colors.blackWithAlpha,
  },
  buttonStyle: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 128,
    height: 32,
    backgroundColor: Colors.white,
    borderRadius: 16,
  },
  modal: {
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 8,
    width: 320,
    height: 400,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  container: {
    flex: 1,
    justifyContent: 'flex-start',
    flexDirection: 'column',
    backgroundColor: Colors.primaryGrayBackground,
    width: '100%',
  },
  scrollViewContainer: {
    flex: 1,
  },
  scrollViewContent: {
    backgroundColor: Colors.primaryGrayBackground,
    flexGrow: 1,
    padding: 12,
  },
  dateFilterRowRadioButton: {
    marginBottom: 12,
    borderRadius: 8,
    borderWidth: 1,
    paddingLeft: 12,
    paddingRight: 6,
    flexDirection: 'column',
    backgroundColor: Colors.white,
    borderColor: Colors.graySpacer,
  },
  rowDateFilterContainer: {
    height: 44,
  },
  rowRadioButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  checkboxLabel: {
    textAlign: 'left',
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: Colors.black,
  },
  renderCustomDate: {
    flexDirection: 'row',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 0,
    paddingBottom: 12,
  },
  cancelButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: 44,
    width: 60,
    maxWidth: 60,
    marginRight: 20,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: Colors.primaryDark,
  },
  downloadButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    height: 44,
    width: 176,
  },
  downloadButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.white,
    lineHeight: 18,
  },
  dateRangeContainer: {
    ...Platform.select({
      web: {
        flexDirection: 'column',
        gap: 12,
      },
    }),
  },
});
